/**
 * Material library: the shared TSL slot materials per (slot, geometry variant), the packed livery per look (M6 P23:
 * liveries are per-object uniforms, so looks never add pipelines), the shader clock, the
 * per-preset tiers and the live tuning group (asset contract §4.5, §8.2). Asset-lane owned (A5).
 * The exported signatures in index.ts are frozen; internals may change freely.
 */
import type { Material, MeshPhysicalNodeMaterial, MeshStandardNodeMaterial, UniformNode, WebGPURenderer } from 'three/webgpu'
import { uniform } from 'three/tsl'
import type { LoadedTextureSet } from '../../assets/loader'
import type { MaterialSlot } from '../../data/assetSchema'
import type { FactionId, QualityPreset, TuningGroup, TuningParam } from '../contracts'
import { materialTierFor, webgl2CapsOf } from '../webgl2'
import { loadMaterialsData, paletteOf, tierOf, wearLookOf, type MaterialsFile, type TuningDefaults } from './data'
import { authoredMaps, createHullContext, createPalette, HERO_VARIANT, trimFamilyOf, variantKey, type HullContext, type PaletteUniforms, type TrimFamily, type Variant } from './hull/context'
import { packLivery, type LiveryColors } from './hull/livery'
import {
  configureDecal,
  configureEmissive,
  configureGlass,
  configureHull,
  createDecalMaterial,
  createEmissiveMaterial,
  createGlassMaterial,
  createHullMaterial,
} from './hull/slots'

export interface MaterialLibrary {
  readonly tuning: TuningGroup
  /** Advances the shared shader clock (strobes, flicker, spin, crack cooling). Call once per frame; pass 0 to freeze (lookdev). */
  update(dt: number): void
}

/** Asset-lane internal view of the library. Other lanes only see MaterialLibrary. */
export interface MaterialLibraryInternals extends MaterialLibrary {
  readonly renderer: WebGPURenderer
  readonly sets: ReadonlyMap<string, LoadedTextureSet>
  readonly data: MaterialsFile
  readonly time: UniformNode<'float', number>
  readonly ctx: HullContext
  preset: QualityPreset
  /** The shared per-object livery uniforms every slot material reads. */
  readonly palette: PaletteUniforms
  /** The slot material for hero geometry (baked ECL_Mask, trim UVs). */
  slotMaterial(slot: MaterialSlot, faction: FactionId): Material
  /** The slot material for a geometry variant (edgeBake.ts `geometryTraits`); the look only picks the trim set. */
  materialFor(slot: MaterialSlot, faction: FactionId, variant: Variant): Material
  /** A look's packed livery (hull/livery.ts), one shared array per look; meshes hold it in `userData[LIVERY_KEY]`. */
  livery(look: FactionId): Float32Array
  /** Every material built so far (tests, lookdev stats). */
  materials(): readonly Material[]
}

export function internals(lib: MaterialLibrary): MaterialLibraryInternals {
  return lib as MaterialLibraryInternals
}

interface CacheEntry {
  readonly material: Material
  readonly slot: MaterialSlot
  readonly family: TrimFamily
  readonly variant: Variant
}

/** Slots whose look depends on the geometry variant (mask bake and trim UVs). */
function variantDependent(slot: MaterialSlot): boolean {
  return slot === 'hull' || slot === 'decal' || slot === 'set'
}

/**
 * One material per slot (× geometry variant for hull, decal and set). The look only matters to trimmed hulls, which
 * sample the first_rush trim set or the Reach one; every livery colour is a per-object uniform (M6 P23, D-252).
 */
export function materialKey(slot: MaterialSlot, faction: FactionId, variant: Variant): string {
  if (!variantDependent(slot)) return slot
  const vk = variantKey(variant)
  return slot !== 'decal' && variant.trimmed ? `${slot}:${trimFamilyOf(faction)}:${vk}` : `${slot}:${vk}`
}

/** Rebuilds every cached material of a library for its current preset. */
const reconfigure = new WeakMap<MaterialLibrary, () => void>()

function tuningParam(value: number, min: number, max: number): TuningParam {
  return { value, min, max, step: 0.01 }
}

function createTuning(ctx: HullContext): TuningGroup {
  const t = ctx.data.tuning
  const params: Record<keyof TuningDefaults, TuningParam> = {
    wear: tuningParam(t.wear, 0, 3),
    grime: tuningParam(t.grime, 0, 3),
    soot: tuningParam(t.soot, 0, 3),
    rim: tuningParam(t.rim, 0, 3),
    clearcoat: tuningParam(t.clearcoat, 0, 1),
    clearcoatRoughness: tuningParam(t.clearcoatRoughness, 0.05, 1),
    panelLines: tuningParam(t.panelLines, 0, 3),
  }
  const keys = Object.keys(params) as (keyof TuningDefaults)[]
  return {
    name: 'Materials',
    params,
    apply(): void {
      for (const k of keys) {
        const v = params[k].value
        if (typeof v === 'number') ctx.tune[k].value = v
      }
    },
  }
}

export function createMaterialLibrary(renderer: WebGPURenderer, sets: ReadonlyMap<string, LoadedTextureSet>, preset: QualityPreset): MaterialLibrary {
  const data = loadMaterialsData()
  const time = uniform(0)
  const ctx = createHullContext(data, sets, time)
  const cache = new Map<string, CacheEntry>()
  const liveries = new Map<FactionId, Float32Array>()
  const liveryOf = (look: FactionId): Float32Array => {
    let l = liveries.get(look)
    if (!l) {
      l = packLivery(paletteOf(data, look), wearLookOf(data, look))
      liveries.set(look, l)
    }
    return l
  }
  // Objects without a livery (lookdev overrides, tests) read the neutral look.
  const palette = createPalette(data, liveryOf('neutral'))
  const maps = authoredMaps(ctx.sets)
  // WebGL2 renders one lean tier on every preset (webgl2.json, D-053).
  const caps = webgl2CapsOf(renderer)

  const configure = (e: CacheEntry): void => {
    const tier = tierOf(data, materialTierFor(lib.preset, caps))
    const pal = palette
    if (e.slot === 'glass') configureGlass(e.material as MeshPhysicalNodeMaterial, ctx, pal)
    else if (e.slot === 'emissive') configureEmissive(e.material as MeshStandardNodeMaterial, ctx, pal)
    else if (e.slot === 'decal') configureDecal(e.material as MeshPhysicalNodeMaterial, ctx, pal, tier, e.variant)
    else configureHull(e.material as MeshPhysicalNodeMaterial, ctx, pal, e.family, tier, e.variant)
  }

  const applyAnisotropy = (): void => {
    const a = tierOf(data, materialTierFor(lib.preset, caps)).anisotropy
    for (const t of maps) {
      if (t.anisotropy === a) continue
      t.anisotropy = a
      t.needsUpdate = true
    }
  }

  const lib: MaterialLibraryInternals = {
    renderer,
    sets,
    data,
    time,
    ctx,
    palette,
    preset,
    tuning: createTuning(ctx),
    update(dt: number): void {
      time.value += dt
    },
    materialFor(slot: MaterialSlot, faction: FactionId, variant: Variant): Material {
      const key = materialKey(slot, faction, variant)
      let e = cache.get(key)
      if (!e) {
        const vk = variantKey(variant)
        // Untrimmed hulls never sample a trim set, so they share the Reach entry whatever the look.
        const family: TrimFamily = variant.trimmed ? trimFamilyOf(faction) : 'reach'
        const material =
          slot === 'glass'
            ? createGlassMaterial()
            : slot === 'emissive'
              ? createEmissiveMaterial()
              : slot === 'decal'
                ? createDecalMaterial(vk, renderer.reversedDepthBuffer === true)
                : createHullMaterial(family, vk)
        material.name = key
        e = { material, slot, family, variant }
        configure(e)
        cache.set(key, e)
      }
      return e.material
    },
    slotMaterial(slot: MaterialSlot, faction: FactionId): Material {
      return lib.materialFor(slot, faction, HERO_VARIANT)
    },
    materials(): readonly Material[] {
      return [...cache.values()].map((e) => e.material)
    },
    livery: liveryOf,
  }
  applyAnisotropy()
  // Preset changes rebuild the node graphs of the cached materials in place (setMaterialPreset).
  reconfigure.set(lib, (): void => {
    for (const e of cache.values()) configure(e)
    applyAnisotropy()
  })
  return lib
}

/**
 * Sets the player corporation's colours (`CorpRecord.livery`) on the `player_corp` look in place: every object
 * wearing that look repaints on its next draw, with no new material or pipeline. Not per frame.
 */
export function setCorpLivery(lib: MaterialLibrary, colors: LiveryColors): void {
  const i = internals(lib)
  packLivery(paletteOf(i.data, 'player_corp'), wearLookOf(i.data, 'player_corp'), i.livery('player_corp'), colors)
}

/** Switches the shading tier (materials.json `presets`): recompiles the shared materials in place. Not per frame. */
export function setMaterialPreset(lib: MaterialLibrary, preset: QualityPreset): void {
  const i = internals(lib)
  if (i.preset === preset) return
  i.preset = preset
  reconfigure.get(lib)?.()
}
